import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Building2, Loader2, RefreshCw, ShieldCheck, Plus, Receipt, Calendar,
  Printer, Download, Trash2, ChevronDown,
} from "lucide-react";
import {
  fetchFranchiseDashboard, fetchSpokes,
  listFranchiseSettings, listRoyaltyInvoices, previewNetSales, createRoyaltyInvoice,
  updateRoyaltyInvoiceStatus, deleteRoyaltyInvoice, fetchRoyaltyInvoicePdfBlob,
} from "@/lib/api";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const fmt = (sym, n) => `${sym}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (y, m) =>
  new Date(y, (m || 1) - 1, 1).toLocaleString(undefined, { month: "short", year: "numeric" });

const ROYALTY_STATUS_OPTIONS = [
  { value: "draft", label: "Pending" },
  { value: "sent", label: "Sent" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
];
const statusLabel = (s) =>
  ROYALTY_STATUS_OPTIONS.find((o) => o.value === s)?.label || "Pending";
const statusBadgeClass = (s) => {
  switch (s) {
    case "paid": return "bg-dotr-primary text-white";
    case "sent": return "bg-dotr-ink text-white";
    case "overdue": return "bg-red-600 text-white";
    case "draft":
    default: return "bg-dotr-yellow text-dotr-ink";
  }
};

const StatTile = ({ label, value, accent = false, testId }) => (
  <div className={`brutal-card p-5 ${accent ? "bg-dotr-ink text-white" : ""}`} data-testid={testId}>
    <div className={`font-mono text-[10px] uppercase tracking-[0.18em] mb-1 ${accent ? "text-white/70" : "text-dotr-inkmuted"}`}>
      {label}
    </div>
    <div className={`font-heading font-semibold text-3xl tracking-tighter ${accent ? "text-white" : "text-dotr-primary"}`}>
      {value}
    </div>
  </div>
);

// ---------- New Royalty Invoice modal ----------
const NewRoyaltyInvoiceModal = ({ franchises, defaultSpoke, onClose, onCreated }) => {
  const now = new Date();
  const [spoke, setSpoke] = useState(defaultSpoke || franchises[0]?.spoke || "");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [preview, setPreview] = useState(null);
  const [override, setOverride] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [notes, setNotes] = useState("");

  const loadPreview = useCallback(async () => {
    if (!spoke) return;
    setLoading(true);
    try {
      const d = await previewNetSales(spoke, year, month);
      setPreview(d);
      setOverride(String(d.net_sales));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  }, [spoke, year, month]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const computed = useMemo(() => {
    if (!preview) return null;
    const net = Number(override) || 0;
    const royalty = +(net * (preview.royalty_pct / 100.0)).toFixed(2);
    const subtotal = +(royalty + preview.tech_support_fee + preview.marketing_levy).toFixed(2);
    const vat = preview.vat_applied ? +(subtotal * 0.20).toFixed(2) : 0;
    const total = +(subtotal + vat).toFixed(2);
    return { net, royalty, subtotal, vat, total };
  }, [preview, override]);

  const create = async () => {
    setCreating(true);
    try {
      await createRoyaltyInvoice({
        spoke,
        period_year: Number(year),
        period_month: Number(month),
        net_sales_override: computed?.net ?? null,
        notes: notes || null,
      });
      toast.success("Royalty invoice created");
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y - 1, y, y + 1];
  }, [now]);

  return (
    <div className="fixed inset-0 z-50 bg-dotr-ink/60 flex items-center justify-center p-4" data-testid="royalty-create-modal">
      <div className="bg-dotr-bg border-2 border-dotr-ink max-w-xl w-full shadow-brutal">
        <div className="flex items-center justify-between border-b-2 border-dotr-ink px-5 py-4">
          <div className="flex items-center gap-2">
            <Receipt size={18} />
            <h3 className="font-heading uppercase tracking-tight text-lg">Generate Royalty Invoice</h3>
          </div>
          <button onClick={onClose} data-testid="royalty-modal-close" className="w-8 h-8 border-2 border-dotr-ink bg-white hover:bg-dotr-muted flex items-center justify-center" aria-label="Close">×</button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Franchise</div>
              <select value={spoke} onChange={(e) => setSpoke(e.target.value)} data-testid="royalty-spoke" className="brutal-input w-full h-10 px-3 bg-white">
                {franchises.map((f) => <option key={f.spoke} value={f.spoke}>{f.spoke}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Month</div>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="royalty-month" className="brutal-input w-full h-10 px-3 bg-white">
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Year</div>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="royalty-year" className="brutal-input w-full h-10 px-3 bg-white">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          </div>

          <div className="brutal-card bg-dotr-muted/40 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted">Auto-calculated Net Sales from orders</div>
              <button type="button" onClick={loadPreview} className="w-8 h-8 border-2 border-dotr-ink bg-white hover:bg-dotr-muted flex items-center justify-center" title="Refresh" aria-label="Refresh">
                {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              </button>
            </div>
            <label className="block">
              <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Net Sales (£) — editable</div>
              <input
                type="number"
                step="0.01"
                min="0"
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                data-testid="royalty-net-sales"
                className="brutal-input w-full h-11 px-3 bg-white"
              />
            </label>
            {preview && computed && (
              <div className="mt-3 border-t-2 border-dotr-ink/20 pt-3 space-y-1 font-mono text-xs">
                <div className="flex justify-between"><span>Royalty ({preview.royalty_pct.toFixed(2)}%)</span><span>{fmt("£", computed.royalty)}</span></div>
                <div className="flex justify-between"><span>Technology &amp; Support</span><span>{fmt("£", preview.tech_support_fee)}</span></div>
                <div className="flex justify-between"><span>Marketing Levy</span><span>{fmt("£", preview.marketing_levy)}</span></div>
                <div className="flex justify-between border-t border-dotr-ink/40 pt-1 mt-1"><span>Subtotal</span><span>{fmt("£", computed.subtotal)}</span></div>
                {preview.vat_applied && <div className="flex justify-between"><span>VAT (20%)</span><span>{fmt("£", computed.vat)}</span></div>}
                <div className="flex justify-between font-heading text-base pt-1"><span>Total</span><span data-testid="royalty-preview-total">{fmt("£", computed.total)}</span></div>
              </div>
            )}
          </div>

          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Notes (optional)</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="royalty-notes"
              rows={2}
              className="brutal-input w-full px-3 py-2 bg-white"
            />
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 border-t-2 border-dotr-ink p-4">
          <button onClick={onClose} className="brutal-btn-ghost px-4 py-2 text-sm">Cancel</button>
          <button
            onClick={create}
            disabled={creating || !spoke}
            data-testid="royalty-create-btn"
            className="brutal-btn px-5 py-2 text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            Create invoice
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Royalty invoices table for a single franchise ----------
const FranchiseInvoicesTable = ({ invoices, onPdf, onStatus, onDelete }) => {
  if (!invoices.length) {
    return (
      <div className="brutal-card p-10 text-center" data-testid="royalty-empty">
        <Receipt size={24} className="mx-auto mb-2 text-dotr-inkmuted" />
        <div className="font-mono text-xs uppercase tracking-widest text-dotr-inkmuted">
          No royalty invoices yet for this franchise.
        </div>
      </div>
    );
  }
  return (
    <div className="brutal-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[880px]" data-testid="royalty-table">
          <thead>
            <tr className="bg-dotr-ink text-white">
              {["Number", "Period", "Net Sales", "Royalty", "Fixed Fees", "Total", "Status", ""].map((h) => (
                <th key={h} className="text-left p-3 font-heading text-[10px] uppercase tracking-[0.14em] border-r border-white/20 last:border-r-0">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} data-testid={`royalty-row-${inv.id}`} className="border-b-2 border-dotr-ink/20 hover:bg-dotr-muted/40">
                <td className="p-3 font-mono text-sm font-bold">{inv.invoice_number}</td>
                <td className="p-3 font-mono text-xs">
                  <div className="flex items-center gap-1"><Calendar size={11} />{MONTHS[(inv.period_month || 1) - 1]} {inv.period_year}</div>
                </td>
                <td className="p-3 font-mono text-xs">{fmt("£", inv.net_sales)}</td>
                <td className="p-3 font-mono text-xs" title={`${(inv.royalty_pct || 0).toFixed(2)}% × ${fmt("£", inv.net_sales)}`}>
                  {fmt("£", inv.royalty_amount)}
                </td>
                <td className="p-3 font-mono text-xs" title="Tech + Marketing">
                  {fmt("£", (inv.tech_support_fee || 0) + (inv.marketing_levy || 0))}
                </td>
                <td className="p-3 font-heading text-sm font-bold text-dotr-primary">{fmt("£", inv.total)}</td>
                <td className="p-3">
                  <div className="relative inline-block">
                    <select
                      value={inv.status || "draft"}
                      onChange={(e) => onStatus(inv, e.target.value)}
                      data-testid={`royalty-status-select-${inv.id}`}
                      title={inv.paid_at ? `Paid at ${new Date(inv.paid_at).toLocaleString()}` : undefined}
                      className={`appearance-none pl-2 pr-6 py-1 border-2 border-dotr-ink font-mono text-[10px] uppercase tracking-widest cursor-pointer focus:outline-none ${statusBadgeClass(inv.status)}`}
                    >
                      {ROYALTY_STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value} className="bg-white text-dotr-ink">{o.label}</option>
                      ))}
                    </select>
                    <ChevronDown size={11} className="absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none opacity-80" />
                  </div>
                </td>
                <td className="p-3">
                  <div className="inline-flex gap-1">
                    <button onClick={() => onPdf(inv)} className="w-9 h-9 border-2 border-dotr-ink bg-white hover:bg-dotr-muted flex items-center justify-center" title="Preview / Print PDF" data-testid={`royalty-view-${inv.id}`} aria-label="Preview"><Printer size={13} /></button>
                    <button onClick={() => onPdf(inv, true)} className="w-9 h-9 border-2 border-dotr-ink bg-white hover:bg-dotr-muted flex items-center justify-center" title="Download PDF" data-testid={`royalty-dl-${inv.id}`} aria-label="Download"><Download size={13} /></button>
                    <button onClick={() => onDelete(inv)} className="w-9 h-9 border-2 border-dotr-ink bg-white hover:bg-dotr-primary hover:text-white flex items-center justify-center" title="Delete" data-testid={`royalty-del-${inv.id}`} aria-label="Delete"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Main page ----------
export const FranchiseAdmin = () => {
  const [dash, setDash] = useState(null);
  const [spokeMap, setSpokeMap] = useState({});
  const [franchises, setFranchises] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [defaultSpoke, setDefaultSpoke] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, sp, fs, inv] = await Promise.all([
        fetchFranchiseDashboard(),
        fetchSpokes(),
        listFranchiseSettings(),
        listRoyaltyInvoices(),
      ]);
      setDash(d);
      const map = {};
      for (const r of (sp?.spokes || [])) map[r.name] = r;
      setSpokeMap(map);
      setFranchises(fs.franchises || []);
      setInvoices(inv || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load franchise dashboard");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const invoicesBySpoke = useMemo(() => {
    const g = {};
    for (const inv of invoices) {
      (g[inv.spoke] = g[inv.spoke] || []).push(inv);
    }
    return g;
  }, [invoices]);

  const openPdf = async (inv, download = false) => {
    try {
      const blob = await fetchRoyaltyInvoicePdfBlob(inv.id);
      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error("Empty PDF response");
      }
      // Ensure correct MIME so some browsers don't block inline viewing
      const pdfBlob = blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener noreferrer";
      if (download) {
        a.download = `${inv.invoice_number}.pdf`;
      } else {
        a.target = "_blank";
      }
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      // Surface the real axios error so admins see useful text
      const detail = e?.response?.status
        ? `Server returned ${e.response.status}`
        : (e?.message || "Unknown error");
      console.error("Royalty PDF failed:", e);
      toast.error(`Could not open PDF — ${detail}`);
    }
  };

  const changeStatus = async (inv, next) => {
    if (next === inv.status) return;
    setInvoices((prev) => prev.map((it) => (it.id === inv.id ? { ...it, status: next } : it)));
    try {
      const updated = await updateRoyaltyInvoiceStatus(inv.id, next);
      setInvoices((prev) => prev.map((it) => (it.id === inv.id ? { ...it, ...updated } : it)));
      toast.success(`Marked as ${statusLabel(next)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status update failed");
      setInvoices((prev) => prev.map((it) => (it.id === inv.id ? { ...it, status: inv.status } : it)));
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete royalty invoice ${inv.invoice_number}? This cannot be undone.`)) return;
    try {
      await deleteRoyaltyInvoice(inv.id);
      toast.success("Royalty invoice deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const openNewFor = (spoke) => {
    setDefaultSpoke(spoke);
    setShowNew(true);
  };

  return (
    <div data-testid="page-franchise-admin" className="space-y-5">
      <div className="brutal-card p-5 md:p-6">
        <div className="flex items-start md:items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 border-2 border-dotr-ink bg-dotr-ink text-white flex items-center justify-center">
              <ShieldCheck size={20} />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-2xl md:text-3xl uppercase tracking-tight">Franchise Admin</h2>
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-dotr-inkmuted">
                Monthly &amp; YTD figures plus royalty invoices for every DUM ON THE RUN<sup>®</sup> franchise
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => { setDefaultSpoke(""); setShowNew(true); }}
              disabled={franchises.length === 0}
              data-testid="btn-new-royalty"
              className="brutal-btn px-5 py-2.5 text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              <Plus size={14} /> Generate royalty invoice
            </button>
            <button onClick={load} disabled={loading} data-testid="btn-refresh-franchise-admin" className="brutal-btn-ghost px-3 py-2.5 text-xs inline-flex items-center gap-1.5">
              {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Refresh
            </button>
          </div>
        </div>
      </div>

      {loading && !dash ? (
        <div className="brutal-card p-16 flex items-center justify-center"><Loader2 size={24} className="animate-spin" /></div>
      ) : !dash?.franchises?.length ? (
        <div className="brutal-card p-16 text-center" data-testid="franchise-admin-empty">
          <Building2 size={30} className="mx-auto mb-3 text-dotr-inkmuted" />
          <div className="font-heading text-xl uppercase tracking-tight mb-1">No franchises yet</div>
          <div className="font-mono text-xs uppercase tracking-widest text-dotr-inkmuted">
            Add one from the Franchise Settings tab to see live figures here.
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {dash.franchises.map((f) => {
            const c = spokeMap[f.spoke] || { color: "#1A1714", fg: "#FFFFFF" };
            const franchiseInvoices = invoicesBySpoke[f.spoke] || [];
            return (
              <div key={f.spoke} data-testid={`franchise-admin-row-${f.spoke}`} className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 border-2 border-dotr-ink font-mono text-[10px] uppercase tracking-widest"
                    style={{ background: c.color, color: c.fg }}
                    data-testid={`franchise-admin-badge-${f.spoke}`}
                  >
                    <Building2 size={11} />
                    {f.spoke}
                  </span>
                  <h3 className="font-heading font-semibold text-sm uppercase tracking-widest">Franchise</h3>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted">
                    {monthLabel(f.year, f.month)}
                  </span>
                  <button
                    onClick={() => openNewFor(f.spoke)}
                    data-testid={`btn-new-royalty-${f.spoke}`}
                    className="ml-auto brutal-btn-ghost px-3 py-1.5 text-[10px] inline-flex items-center gap-1 uppercase tracking-widest"
                  >
                    <Plus size={11} /> Royalty invoice
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatTile label={`Net sales · ${monthLabel(f.year, f.month)}`} value={fmt("£", f.mtd_net_sales)} testId={`franchise-mtd-${f.spoke}`} />
                  <StatTile label="YTD net sales" value={fmt("£", f.ytd_net_sales)} testId={`franchise-ytd-net-${f.spoke}`} accent />
                  <StatTile label={`YTD royalty earned (${(f.royalty_pct || 0).toFixed(1)}%)`} value={fmt("£", f.ytd_royalty_earned)} testId={`franchise-ytd-royalty-${f.spoke}`} />
                  <StatTile label="Outstanding balance" value={fmt("£", f.outstanding_balance)} testId={`franchise-outstanding-${f.spoke}`} accent />
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <Receipt size={14} />
                  <h4 className="font-heading font-semibold text-sm uppercase tracking-widest">
                    Royalty Invoices
                  </h4>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted" data-testid={`royalty-count-${f.spoke}`}>
                    {franchiseInvoices.length} total
                  </span>
                </div>
                <FranchiseInvoicesTable
                  invoices={franchiseInvoices}
                  onPdf={openPdf}
                  onStatus={changeStatus}
                  onDelete={remove}
                />
              </div>
            );
          })}
        </div>
      )}

      {showNew && (
        <NewRoyaltyInvoiceModal
          franchises={franchises}
          defaultSpoke={defaultSpoke}
          onClose={() => setShowNew(false)}
          onCreated={load}
        />
      )}
    </div>
  );
};

export default FranchiseAdmin;
